import { useEffect } from 'react'

import type { VuiComponent } from '../core'
import type { InputProps } from './input.types'

import { vui } from '../core'
import { TextField } from '../textField'
import { inputStateMapping } from './consts'
import HelpText from './helpText'
import InputIcon from './inputIcon'
import InputInput from './inputInput'

/**
 * @deprecated Use `TextField` instead. `Input` is a thin wrapper kept for backward compatibility and
 * will be removed in a future major version.
 */
export const Input = vui<'div', InputProps>((props, ref) => {
  const {
    children,
    iconLeft,
    iconRight,
    input,
    inputProps,
    inputRef,
    startIcon,
    endIcon,
    state,
    stateMapping,
    ...rest
  } = props

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[Input] is deprecated. Use `TextField` instead.')
      if (iconLeft !== undefined) {
        console.warn('[Input] `iconLeft` is deprecated. Use `startIcon` instead.')
      }
      if (iconRight !== undefined) {
        console.warn('[Input] `iconRight` is deprecated. Use `endIcon` instead.')
      }
      if (state !== undefined) {
        console.warn('[Input] `state` is deprecated. Use `variant`, `errorText` or `loading` on `TextField` instead.')
      }
    }
  }, [iconLeft, iconRight, state])

  // legacy `state` prop: merged mapping renders the state icon after the end icon (old render order);
  // `loading` is handled natively by TextField's `loading` prop
  const states = { ...inputStateMapping, ...stateMapping }
  const stateIconProps = state && state !== 'loading' ? states[state]?.iconProps : undefined
  const resolvedEndIcon = endIcon ?? iconRight
  const endIconWithState =
    resolvedEndIcon && stateIconProps ? (
      <>
        {resolvedEndIcon}
        <TextField.Icon {...stateIconProps} />
      </>
    ) : stateIconProps ? (
      <TextField.Icon {...stateIconProps} />
    ) : (
      resolvedEndIcon
    )

  return (
    <TextField
      endIcon={endIconWithState}
      field={children ?? input}
      fieldProps={inputProps}
      fieldRef={inputRef}
      loading={state === 'loading'}
      ref={ref}
      startIcon={startIcon ?? iconLeft}
      {...rest}
    />
  )
}) as VuiComponent<'div', InputProps> & {
  Icon: typeof InputIcon
  Input: typeof InputInput
  HelpText: typeof HelpText
}

Input.Icon = InputIcon
Input.Input = InputInput
Input.HelpText = HelpText
Input.displayName = 'Input'

export default Input
